import PropTypes from 'prop-types';
import React, {useState} from 'react';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { IC_DOCUMENT_GREEN, IC_LEFT_PAGINATION_ARROW_GREEN, IC_RIGHT_PAGINATION_ARROW_GREEN } from '@common/Assets/images';
import BlankCard from '@common/Component/BlankCard/BlankCard';
import CustomModal from '@common/Component/Modal/Modal';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';
import { checkIfCurrentLangIsArabic, getAppConfigDataByLang } from '@common/Utility/common-utils';
import { isRTL, useTranslation } from '@common/i18n';
import { getLangCodeFromUrl } from '../../../../i18n';

const showLimit = 2;
const ProductOverview = ({ detailsData, scrollRef, fbtCondition }) => {
  const generateURL = useNavigationURL();
  const t = useTranslation();
  const appConfig = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const [overviews, setOverviews] = React.useState(detailsData?.extensionAttributes?.overview ?? []);
  const [showSpecs, setShowSpecs] = useState(false);
  const [currentLang, setCurrentLanguage] = React.useState('');
  const [infoModalData, setInfoModalData] = React.useState({ visible: false });

  React.useEffect(() => {
    if(window !== 'undefined'){
      setCurrentLanguage(getLangCodeFromUrl());
    }
  }, []);

  React.useEffect(() => {
    if (!showSpecs && detailsData?.extensionAttributes?.overview?.length > showLimit) {
      setOverviews(detailsData?.extensionAttributes?.overview?.slice(0, showLimit));
    } else {
      setOverviews(detailsData?.extensionAttributes?.overview);
    }
  }, [showSpecs, detailsData?.extensionAttributes?.overview]);

  const showModal = () => {
    const popupContent = getAppConfigDataByLang(appConfig, 'legal_disclaimer_content', currentLang );

    setInfoModalData({
      visible: true,
      data: {
        title: t('ProductDetail.legaldisclaimer'),
        description: popupContent,
        cta: generateURL('/faq/returns&refunds'),
      },
    });
  };

  const showOverviewItem = (attributeCode) => {
    const item = overviews?.find((itemIn) => itemIn?.attributeCode === attributeCode);
    if (item?.attributeLabel && item?.label) {
      return (
        <div className="overviewItems">
          <h2>{item?.attributeLabel === 'Short Description' ? t('ProductDetail.Information') : item?.attributeLabel}</h2>
          <div dangerouslySetInnerHTML={{ __html: item?.label }} className="detailsBox" />
        </div>
      );
    }
  };
  const scrollToOverview = () => {
    if (showSpecs) {
      const element = document?.getElementById('overviewSection');
      const headerOffset = 100;
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {detailsData?.extensionAttributes && (
        <div id="overviewSection">
          <BlankCard title={t('ProductDetail.Overview')} border customClass="mb-3">
            {fbtCondition && <div ref={scrollRef} id="fbtSection" />}
            <div className="information-wrapper">
              {overviews?.length > 0 && (
                <>
                  {showOverviewItem('short_description')}
                  {showOverviewItem('description')}

                  {showSpecs && (
                    <>
                      {showOverviewItem('benefits')}
                      {showOverviewItem('how_to_use')}
                    </>
                  )}
                </>
              )}
            </div>
          </BlankCard>
        </div>
      )}
      {showSpecs && detailsData?.extensionAttributes?.specifications?.length > 0 && (
        <>
          <BlankCard customClass="specificationsBox" title="Specifications" border>
            <div className="specs">
              {detailsData?.extensionAttributes?.specifications?.map((items, index) => (
                <div key={index} className="specRow">
                  <div className="labelText">{items?.attributeLabel}</div>
                  <div className="valueText">{items?.label}</div>
                </div>
              ))}
            </div>
            {typeof window !== 'undefined' && checkIfCurrentLangIsArabic() ? (<div 
              className="legalDisclaimerClick" 
              onClick={showModal} 
              style={{
                background: `url(${IC_LEFT_PAGINATION_ARROW_GREEN.src}) no-repeat 24px 50% / 8px` 
                }}
              >
              <div className="ic">
                <Image src={IC_DOCUMENT_GREEN?.src} width="26px" height="26px" alt="" />
              </div>
              <div className="text">{t('ProductDetail.legaldisclaimer')}</div>
            </div>) : (<div 
              className="legalDisclaimerClick" 
              onClick={showModal} 
              style={{
                background: `url(${IC_RIGHT_PAGINATION_ARROW_GREEN.src}) no-repeat calc(100% - 24px) 50% / 8px` 
                }}
              >
              <div className="ic">
                <Image src={IC_DOCUMENT_GREEN?.src} width="26px" height="26px" alt="" />
              </div>
              <div className="text">{t('ProductDetail.legaldisclaimer')}</div>
            </div>)}
          </BlankCard>
          {infoModalData?.visible === true && infoModalData?.data && (
            <CustomModal
              titleLeftAligned={currentLang && !isRTL(currentLang) }
              titleRightAligned={currentLang && isRTL(currentLang) }
              footerBtnFullWidth
              modalTitle={infoModalData?.data?.title}
              size="lg"
              noFooter
              customClass="modal_pdpAdvtBox"
              contentMaxHeight="400px"
              closeButton
              crossPressed={() => {
                setInfoModalData({ visible: false, data: null });
              }}
            >
              {infoModalData?.data?.description}
              {infoModalData?.data?.cta && infoModalData?.data?.cta !== '' && (
                <div>
                  <a target="_blank" rel="noreferrer" href={infoModalData?.data?.cta}>
                    {t('ProductDetail.readmore')}
                  </a>
                </div>
              )}
            </CustomModal>
          )}
        </>
      )}
      {(overviews?.length > 2 || detailsData?.extensionAttributes?.specifications?.length > 0) && (
        <div
          className="showSpecs"
          onClick={() => {
            setShowSpecs(!showSpecs);
            scrollToOverview();
          }}
        >
          {showSpecs ? t('ProductDetail.showLess') : t('ProductDetail.showMore')}
        </div>
      )}
    </>
  );
};

ProductOverview.propTypes = {
  detailsData: PropTypes.object,
  scrollRef: PropTypes.object,
  fbtCondition: PropTypes.bool,
};

ProductOverview.defaultProps = {
  detailsData: {},
  scrollRef: {},
  fbtCondition: false,
};

export default ProductOverview;
